import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CalendarIcon, ClockIcon } from 'lucide-react';
type ContentBlock = {
  type: 'paragraph';
  text: string;
} | {
  type: 'heading';
  text: string;
} | {
  type: 'subheading';
  text: string;
} | {
  type: 'image';
  src: string;
  alt: string;
} | {
  type: 'list';
  items: string[];
} | {
  type: 'ordered-list';
  items: string[];
} | {
  type: 'source-list';
  items: {
    text: string;
    url: string;
  }[];
};
const posts: Record<string, {
  title: string;
  date: string;
  readTime: string;
  category: string;
  image: string;
  author?: string;
  blocks: ContentBlock[];
  ctaTitle?: string;
  ctaDescription?: string;
  ctaLink?: string;
  ctaLabel?: string;
}> = {
  'we-built-a-custom-erp-at-a-rocket-factory': {
    title: "We Built a Custom ERP at a Rocket Factory. Here's What We Learned.",
    date: 'March 9, 2026',
    readTime: '18 min read',
    category: 'Company',
    author: 'Alex',
    image: 'https://images.unsplash.com/photo-1541185933-ef5d8ed016c2?auto=format&fit=crop&q=80&w=1200&h=600',
    blocks: [{
      type: 'paragraph',
      text: "How Astra Space built AstraOS — inspired by SpaceX's Warp Drive — and why we're bringing that playbook into the AI era at Thruline."
    }, {
      type: 'paragraph',
      text: 'In 2011, a small team of five engineers at SpaceX shipped something that would quietly become one of the most important pieces of software in aerospace: Warp Drive, a proprietary ERP system that managed everything from engineering design to supply chain, manufacturing execution, mission operations, and costing — all under one roof. [1][2]'
    }, {
      type: 'paragraph',
      text: "It wasn't flashy. It wasn't a product you could buy. But Warp Drive became the nervous system of a company that would go on to redefine what a manufacturing organization could look like. As the SpaceX Application Software team described it, their ERP covered every stage of building a rocket — from purchasing and receiving raw material to creating and executing work orders, tracking quality escapes and change management, and implementing procedures all the way to launch. [3]"
    }, {
      type: 'paragraph',
      text: "As Palantir's CTO Shyam Sankar put it when his company launched its own manufacturing platform, all the founders driving reindustrialization understand this — it's the reason SpaceX built their own ERP. [4]"
    }, {
      type: 'paragraph',
      text: 'I know this firsthand. Because a few years later, I helped build one too.'
    }, {
      type: 'heading',
      text: 'Astra Space and the Dream of Daily Launches'
    }, {
      type: 'paragraph',
      text: 'Astra Space was founded in Alameda, California, with an audacious vision: make orbital launch as routine as a FedEx shipment. Founded by Chris Kemp and Dr. Adam London, the company designed small, low-cost rockets built from welded sheet aluminum — no fancy turbomachinery, no exotic materials. The idea was to make rockets so cheap and so fast to produce that you could eventually launch one every single day. [5]'
    }, {
      type: 'paragraph',
      text: 'The company came out of stealth in 2020, went public via SPAC at a $2.1 billion valuation in 2021, and became the fastest company in history to reach orbit — just five years after the founding of the launch vehicle program. [6][7]'
    }, {
      type: 'paragraph',
      text: "But here's what most people don't know about Astra: the ambition wasn't just in the rockets. It was in how we ran the factory."
    }, {
      type: 'heading',
      text: 'Enter AstraOS'
    }, {
      type: 'paragraph',
      text: "When you're trying to build rockets at the pace Astra was targeting — weekly, then daily launches — the software that runs your operations becomes just as critical as the propulsion system."
    }, {
      type: 'paragraph',
      text: "At Astra, we called our internal system AstraOS. As CEO Chris Kemp described it publicly: \"AstraOS is our financial systems. It's our systems that help engineers do their product development. It's our program management systems. It's all the core software that runs the business. So it's the operating system of Astra.\" [8]"
    }, {
      type: 'paragraph',
      text: 'We were explicitly trying to replicate what SpaceX had done with Warp Drive. The thesis was simple: if you want to build hardware at software speed, you need software that actually understands how your hardware gets built.'
    }, {
      type: 'paragraph',
      text: 'AstraOS was designed to track every part from raw material to flight hardware, understand what each component cost, how much labor went into it, what the lead time was, which machine touched it, and how all of that rolled up into the economics of a launch. The system was tied to every single step of the process.'
    }, {
      type: 'paragraph',
      text: "We built it using a combination of custom development and off-the-shelf tools — stitching together the financial backbone, manufacturing execution, inventory management, and engineering workflows into something coherent. It wasn't a single monolithic application. It was a platform — a collection of integrated systems that gave us visibility across the entire operation."
    }, {
      type: 'heading',
      text: 'Why Not Just Buy an ERP?'
    }, {
      type: 'paragraph',
      text: 'This is the question I get asked most. SAP exists. NetSuite exists. Epicor, Odoo, Infor — the list goes on. Why would a rocket company burn engineering cycles building its own business software?'
    }, {
      type: 'paragraph',
      text: 'The answer comes down to three things:'
    }, {
      type: 'ordered-list',
      items: ["Speed of iteration. Traditional ERP implementations take 6–18 months. At Astra, our rocket designs were changing on a weekly basis. We couldn't wait for a consultant to reconfigure SAP every time we changed a bill of materials. We needed systems that could evolve as fast as our products.", "Manufacturing-first design. As Palantir's Sankar noted, ERP was historically designed for the CFO, not the factory floor. [4] When you're tracking rocket parts through fabrication, assembly, test, and integration — and every single one of those parts has to be traceable for flight certification — the standard \"purchase order to invoice\" workflow doesn't cut it. You need a system built around how things actually get made.", "Integration depth. SpaceX's Application Software team sat next to the Starlink assembly line and even took shifts working the different stations to understand the challenges firsthand. [3] That kind of tight coupling between software and operations is impossible when your ERP is a black box maintained by an external vendor."]
    }, {
      type: 'paragraph',
      text: "The SpaceX team eventually moved from Warp Drive's monolithic architecture to microservices when they built systems for Starlink — creating a separate MES, telemetry system, and customer-facing applications, each decoupled but connected via APIs. [3] That evolution from monolith to modular platform is a journey most manufacturers never even get to start."
    }, {
      type: 'heading',
      text: 'What We Actually Built at Astra'
    }, {
      type: 'paragraph',
      text: 'At Astra, AstraOS touched every department:'
    }, {
      type: 'paragraph',
      text: 'Financial tracking at the part level. Every component on a rocket had a cost profile that included raw materials, machine time, labor hours, and overhead allocation. When leadership asked "what does it cost to launch a rocket?" we had a real answer, not a spreadsheet estimate.'
    }, {
      type: 'paragraph',
      text: 'Manufacturing execution. Work orders, routing, shop floor tracking. When a part moved from the machine shop to assembly, the system knew. When an engineer flagged a non-conformance, it triggered a workflow that tracked the issue through resolution.'
    }, {
      type: 'paragraph',
      text: 'Engineering and program management. Product development workflows, change management, and the endless documentation that comes with building flight hardware in a regulated environment.'
    }, {
      type: 'paragraph',
      text: 'Supply chain visibility. Lead times, vendor performance, inventory levels — all feeding into production planning so we could keep the factory moving.'
    }, {
      type: 'paragraph',
      text: 'The key insight was that all of these systems needed to talk to each other in real time. A design change in engineering needed to instantly propagate to manufacturing (new work instructions), supply chain (updated BOM), and finance (revised cost estimates). That kind of cross-functional integration is what separates a custom platform from a patchwork of disconnected tools.'
    }, {
      type: 'heading',
      text: 'The Hard Parts'
    }, {
      type: 'paragraph',
      text: "Let me be honest: AstraOS didn't work perfectly."
    }, {
      type: 'paragraph',
      text: 'Building custom software is expensive. It requires a dedicated team of engineers who understand both software and manufacturing — a rare combination. At SpaceX, the Warp Drive team started with five engineers but grew significantly as the system expanded. At Astra, we were always resource-constrained, which meant making constant tradeoffs between features we wanted and features we needed.'
    }, {
      type: 'paragraph',
      text: 'But the biggest lesson — the one that would later shape everything we do at Thruline — is that the software is only half the problem. The other half is the business process logic.'
    }, {
      type: 'paragraph',
      text: "At Astra, we didn't just need code. We needed to deeply understand how every process worked: how a purchase requisition turned into a PO, how a work order flowed through the shop, what happened when a part failed inspection, how an engineering change order cascaded through a live production schedule. Every one of those processes had edge cases, exceptions, and audit trail requirements that don't show up in a demo or a wireframe. You can't vibe-code your way through a manufacturing ERP — not because the UI is hard, but because the business logic is deep, branching, and full of compliance requirements that vary by industry, customer, and regulation."
    }, {
      type: 'paragraph',
      text: 'Maintenance is relentless. Every process change, every new product line, every organizational shift requires updates to the system. When you own the software, you own every bug, every edge case, every late-night production issue.'
    }, {
      type: 'paragraph',
      text: 'And perhaps most importantly: the knowledge required to build and maintain a custom ERP is deeply specialized. You need people who understand manufacturing processes, financial accounting, supply chain logistics, quality management, AND software architecture. Those people are extraordinarily hard to find and retain.'
    }, {
      type: 'paragraph',
      text: "This is why, despite SpaceX proving the model, very few manufacturers have fully replicated it. The barrier isn't the technology — it's the commitment to continual requirements maintenance, translated into updated and upgraded systems."
    }, {
      type: 'heading',
      text: 'The Pattern Is Spreading'
    }, {
      type: 'paragraph',
      text: "That said, we're starting to see the SpaceX playbook spread — especially in aerospace and defense."
    }, {
      type: 'paragraph',
      text: "Apex has built a proprietary operating system called Octopus that integrates their full spacecraft manufacturing process — linking inventory management, production scheduling, and assembly work instructions into a single, unified environment. [9] Their CTO Max Benassi spent most of his career at SpaceX, and the DNA shows. [10] Apex's Factory One in LA is designed for over 200 satellites per year — that kind of throughput is only possible when your software and your factory floor are tightly coupled. [11]"
    }, {
      type: 'paragraph',
      text: "K2 Space is taking a similar approach. The Torrance-based company is building high-power satellite platforms with vertically integrated hardware and manufacturing — developing key components like reaction wheels, flight computers, and propulsion systems in-house because the existing supply chain simply doesn't support their class of satellite. [12] When you're building that much of the stack yourself, you inevitably need custom manufacturing systems to manage it all."
    }, {
      type: 'paragraph',
      text: "At Thruline, we believe every advanced manufacturer will eventually need some version of this — their own system of record that fits how they actually build. But we don't believe every manufacturer should have to rebuild the software infrastructure from scratch to get there."
    }, {
      type: 'heading',
      text: 'From AstraOS to the ERP Factory: The Thruline Thesis'
    }, {
      type: 'paragraph',
      text: "This is where my story connects to what we're building at Thruline."
    }, {
      type: 'paragraph',
      text: "After years of building custom manufacturing systems — at Astra and through subsequent ERP consulting engagements — I kept running into the same pattern. Every manufacturer I worked with had the same fundamental problem: their business systems didn't match how they actually operated. But they didn't have the engineering team to build something custom, and the traditional ERP implementation model (hire consultants for 12 months, spend six figures, hope for the best) was failing them."
    }, {
      type: 'paragraph',
      text: "Here's what we realized building Thruline: you actually need the requirements context before you can build anything meaningful. The difference between building a manufacturing ERP and vibe-coding a web app isn't the UI complexity — it's that you have to handle hundreds of business edge cases, audit trails, compliance requirements, approval workflows, and process exceptions that are unique to every manufacturer. You can't prompt your way through that without deep domain understanding."
    }, {
      type: 'paragraph',
      text: "That's why a core part of Thruline's service is actually building out the requirements infrastructure within an organization — documenting processes, mapping edge cases, defining business rules — and delivering that as part of the platform. The requirements context is the product, as much as the software is."
    }, {
      type: 'paragraph',
      text: "At Thruline, we're taking the lessons from AstraOS and SpaceX's Warp Drive and bringing them into the AI era."
    }, {
      type: 'paragraph',
      text: "Our thesis is this: The ERP industry has been handcrafting one-off systems for forty years. The ERP Factory era starts now. We believe the same principles driving the Software Factory movement in development — machine ingestion of primary sources, traceable production, expert quality control, automated documentation, compounding knowledge — apply to ERP. The spec for an ERP system isn't technical, it's operational. The \"requirements\" aren't features to be built — they're business processes to be understood, captured, and encoded. That understanding has always been trapped inside discovery meetings, consultant notebooks, and the heads of people who've been doing the job for twenty years. The ERP Factory unlocks it by reading the evidence a company already produces — every email, spreadsheet, PDF, SOP, and inspection checklist — and reconstructing the process at machine scale."
    }, {
      type: 'paragraph',
      text: "We use AI agents to accelerate what used to require months of consulting: gathering requirements from stakeholders, documenting business processes, generating implementation plans, and configuring ERP systems. What took my team weeks of interviews and whiteboarding at Astra can now be compressed into hours. But the insight is that the speed-up isn't just in the software development — it's in the business process logic. We're building a platform that speeds up both simultaneously."
    }, {
      type: 'paragraph',
      text: "The customers we're going after are manufacturers who wouldn't otherwise have the technical expertise to build their own system of record:"
    }, {
      type: 'paragraph',
      text: 'For manufacturers with no developers, Thruline provides the same kind of integrated, manufacturing-first platform that SpaceX and Astra built — but delivered as a service, powered by AI, and configured to their specific processes. We give them best-in-class processes, and the digital backbone to update both the business requirements as they change and the software used to maintain those updated processes.'
    }, {
      type: 'paragraph',
      text: 'For manufacturers with overworked IT and dev teams, we provide the business logic platform and the requirements infrastructure so their internally stretched development teams have the technical foundation to customize in the AI era — without rebuilding from scratch.'
    }, {
      type: 'heading',
      text: 'Why This Matters Now'
    }, {
      type: 'paragraph',
      text: "The timing isn't accidental. Three things have converged:"
    }, {
      type: 'paragraph',
      text: "AI has fundamentally changed what's possible. The requirements gathering, process documentation, and system configuration that used to require expensive consultants can now be augmented (and in some cases automated) by AI agents. This doesn't eliminate expertise — it amplifies it."
    }, {
      type: 'paragraph',
      text: "The reindustrialization movement is real. Whether it's defense manufacturing, semiconductor fabs, or EV battery plants, America is building factories again. Every one of those factories needs business systems, and they can't wait 18 months for a traditional ERP implementation."
    }, {
      type: 'paragraph',
      text: 'The "build vs. buy" calculus has shifted. It used to be that building custom was only viable for SpaceX-scale organizations. With modern development tools, AI-assisted implementation, and composable software architectures, it\'s now possible to give mid-market manufacturers a level of system customization that was previously reserved for companies with nine-figure IT budgets.'
    }, {
      type: 'heading',
      text: 'The Punchline'
    }, {
      type: 'paragraph',
      text: "At Astra, we built AstraOS because we had to. Off-the-shelf tools couldn't keep up with the pace of rocket manufacturing. At SpaceX, Warp Drive exists for the same reason — and Tesla later adapted the concept into its own Warp system for automotive manufacturing. [13] Now companies like Apex and K2 Space are building their own manufacturing operating systems as they scale satellite production."
    }, {
      type: 'paragraph',
      text: "The pattern is clear: the best manufacturers in the world build their own business software because existing ERP systems were designed for a different era. But this isn't something you can solve with vibe coding. Manufacturing ERPs aren't CRUD apps — they're complex systems of business logic, audit trails, compliance rules, and process exceptions that require deep domain understanding to get right."
    }, {
      type: 'paragraph',
      text: "That's why we built Thruline: to bootstrap these types of companies and give them their own manufacturing system — without asking them to hire 300 engineers or spend 18 months in a traditional implementation."
    }, {
      type: 'paragraph',
      text: "At Thruline, we're making that capability accessible to everyone else. We deliver the requirements infrastructure and the technical platform together, so manufacturers can have their own system of record from day one."
    }, {
      type: 'paragraph',
      text: "The factory of the future doesn't run on software designed in the 1970s for the CFO's office. It runs on systems built for the production floor, configured by AI, and owned by the people who actually make things."
    }, {
      type: 'paragraph',
      text: "That's the ERP Factory. And we're just getting started."
    }, {
      type: 'heading',
      text: 'Sources'
    }, {
      type: 'source-list',
      items: [{
        text: 'Nancy Dandridge, LinkedIn — "Back in 2011 we launched Warp Drive — the home grown ERP system for SpaceX."',
        url: 'https://www.linkedin.com/posts/nancydandridge_building-the-software-that-helps-build-spacex-activity-6800839267422760960-eZ0n'
      }, {
        text: 'SpaceX Applications Product Manager Job Listing — "WARPDRIVE is our proprietary ERP system, which manages the company\'s engineering design, supply chain, manufacturing, mission, and costing activities."',
        url: 'https://www.wayup.com/i-j-spacex-673644205606753/'
      }, {
        text: 'Stack Overflow, "Building the software that helps build SpaceX" (May 2021) — Deep dive into SpaceX\'s Application Software team, Warp Drive architecture, and the transition to microservices for Starlink.',
        url: 'https://stackoverflow.blog/2021/05/13/building-the-software-that-helps-build-spacex/'
      }, {
        text: 'Constellation Research, "Palantir Q2 shines as US enterprise growth accelerates, eyes manufacturing, ERP with Warp Speed" — Palantir CTO Shyam Sankar on why SpaceX built their own ERP.',
        url: 'https://www.constellationr.com/blog-news/insights/palantir-q2-shines-us-enterprise-growth-accelerates-eyes-manufacturing-erp-warp'
      }, {
        text: 'Wikipedia, "Astra (American spaceflight company)" — Company history, rocket design philosophy, and the founding team.',
        url: 'https://en.wikipedia.org/wiki/Astra_(American_spaceflight_company)'
      }, {
        text: 'Payload Space, "Inside Astra\'s Rocket Reinvention" (October 2024) — CEO Chris Kemp on Astra\'s journey from SPAC to going private.',
        url: 'https://payloadspace.com/inside-astras-rocket-reinvention/'
      }, {
        text: 'Astra About Page — Company milestones and launch history.',
        url: 'https://astra.com/about'
      }, {
        text: 'NASASpaceFlight.com, "Astra CEO Chris Kemp previews Rocket 4.0, daily launches, and a smarter planet" (June 2021) — Chris Kemp\'s public description of AstraOS.',
        url: 'https://www.nasaspaceflight.com/2021/06/astra-ceo-chris-kemp-previews-rocket-4-0-daily-launches-and-a-smarter-planet/2/'
      }, {
        text: "Apex Space Homepage — Description of Octopus, Apex's proprietary manufacturing operating system.",
        url: 'https://www.apexspace.com/'
      }, {
        text: 'Apex Space, "Our Story" — CTO Max Benassi\'s background at SpaceX and Apex\'s founding.',
        url: 'https://www.apexspace.com/our-story'
      }, {
        text: 'Apex Space, "Factory One" — Details on Apex\'s high-rate satellite manufacturing facility with 200+ satellite/year capacity.',
        url: 'https://www.apexspace.com/factory-one'
      }, {
        text: 'SpaceNews, "K2 Space plans first launch as company wins customers for its large satellite bus" (June 2024) — K2 Space\'s vertically integrated approach.',
        url: 'https://spacenews.com/k2-space-plans-first-launch-as-company-wins-customers-for-its-large-satellite-bus/'
      }, {
        text: 'Electrek, "Tesla claims a software engineer stole critical automated software from its WARP Drive system" (January 2021) — Tesla\'s Warp Drive system as their internal ERP/business automation platform.',
        url: 'https://electrek.co/2021/01/23/tesla-claims-software-engineer-stolen-critical-automated-software-warp-drive-system/'
      }]
    }, {
      type: 'paragraph',
      text: "Alex is the founder of Thruline, where he's building the ERP Factory — using AI agents to give manufacturers custom business systems without needing a fully technical team. He previously built manufacturing software at Astra Space and writes the Everything ERP newsletter. You can find him at thruline.co."
    }],
    ctaTitle: 'Build your own manufacturing system',
    ctaDescription: 'See how Thruline can give your team a custom ERP — without hiring 300 engineers or waiting 18 months.',
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Talk to Our Team'
  },
  'manufacture-your-way-not-theirs': {
    title: 'Manufacture Your Way, Not Theirs',
    date: 'July 8, 2025',
    readTime: '4 min read',
    category: 'Manufacturing',
    author: 'Alex',
    image: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/686c78e06c5d68dee8d6bca4_lenny-kuhne-jHZ70nRk7Ns-unsplash.jpeg',
    blocks: [{
      type: 'paragraph',
      text: "One of the primary problems with off-the-shelf manufacturing software is that it requires developers to customize the system to function within a window of manufacturing specifications. Even if successful, the cost of completing such a customization is either very high or leads to an incomplete product. Oftentimes, companies will be told they should change their manufacturing processes to match the systems they're using, so that their systems will function better. Changing your process to match a software system takes away one of the greatest competitive edges your company has — its own process for doing things. Different industries and different products require unique manufacturing processes, and your software systems should reflect that."
    }, {
      type: 'paragraph',
      text: 'We\'ve heard companies being sold on software that will help them manufacture the "SpaceX way", or will help streamline their factories the "Toyota way". While both SpaceX and Toyota are successful manufacturing companies, these phrases conceal what made each successful: innovative manufacturing processes for their distinct business models and industries that were matched with tailored systems. It\'s not enough to emulate a company, you must ensure your product and business align with the processes you\'re implementing.'
    }, {
      type: 'paragraph',
      text: 'SpaceX is a rocket company, but they are building high fidelity, relatively low production rockets. Their manufacturing process is therefore set up to ensure that the highest quality of manufacturing is achieved. Alternatively, if a rocket company were building many rockets, their model might focus on consistent mass production. You can use the same comparison with Toyota. Toyota has 19 car models and 5 different brands. Newer car manufacturing startups, such as Tesla or Rivian, can find efficiency in their manufacturing process simply because their constraints are different.'
    }, {
      type: 'paragraph',
      text: 'To match their new manufacturing processes, Tesla and SpaceX built a custom manufacturing system called Warp Drive. While it initially started off as a single system between the two companies, it eventually diverged into two different systems because the requirements of rocket manufacturing and car manufacturing have entirely different sets of operating needs. This is an example of ensuring the system you are using is tailored to your manufacturing process, generating additional efficiencies for your business.'
    }, {
      type: 'paragraph',
      text: 'At Thruline, we want to turn your special manufacturing process into a system that gives you the tools you need and the competitive advantage required to succeed in your manufacturing industry. Our tailored configurations allow a completely different manufacturing setup for a car manufacturer as a rocket vehicle company, configurable by analysts not coders. More importantly, we want to ensure that your manufacturing process can change as your business changes. We ensure that your workflows are customizable, not hard coded. Adding additional executive levels? Check out how easy our approval process is to change. Adding a new QA process? Edit a workflow to suit your needs. Thruline is here at every step of your manufacturing journey, ensuring that your product and industry is customizable as you scale.'
    }],
    ctaTitle: 'Build software that fits your process',
    ctaDescription: 'See how Thruline can adapt to your unique manufacturing workflows — not the other way around.',
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Talk to Our Team'
  },
  'custom-erp-vs-pre-configured-solutions-which-is-better-for-your-manufacturing-business': {
    title: 'Custom ERP vs Pre-Configured Solutions: Which Is Better For Your Manufacturing Business?',
    date: 'September 29, 2025',
    readTime: '10 min read',
    category: 'ERP',
    author: 'Alex',
    image: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/68dad5a0f6141102b92506e9_customERPImage.jpeg',
    blocks: [{
      type: 'paragraph',
      text: "When it comes to choosing an ERP system for your manufacturing business, you're facing one of the most critical technology decisions that will impact your operations for years to come. The choice between a custom ERP solution and a pre-configured system isn't just about software: it's about aligning your technology infrastructure with your manufacturing processes, growth plans, and competitive strategy."
    }, {
      type: 'paragraph',
      text: 'Manufacturing businesses operate in a unique environment where production workflows, inventory management, batch tracking, and regulatory compliance create complex requirements that generic software often struggles to address. This complexity makes the ERP selection process particularly challenging, as the wrong choice can lead to inefficient workarounds, productivity bottlenecks, and missed growth opportunities.'
    }, {
      type: 'heading',
      text: 'Understanding Custom ERP Solutions'
    }, {
      type: 'paragraph',
      text: 'Custom ERP systems are built from the ground up to match your specific manufacturing processes, workflows, and business requirements. Rather than adapting your operations to fit pre-built software, custom solutions adapt to how you actually run your business.'
    }, {
      type: 'subheading',
      text: 'Core Strengths of Custom ERP'
    }, {
      type: 'paragraph',
      text: 'Process Integration Excellence — Custom ERP systems excel at integrating with your existing manufacturing workflows, approval chains, and compliance requirements. If your production line uses specialized machinery or follows unique quality control processes, a custom solution can be designed to capture and optimize these exact workflows without forcing compromises.'
    }, {
      type: 'paragraph',
      text: 'Competitive Differentiation — When your ERP system is built specifically for your operations, it becomes a competitive advantage rather than a commodity tool. Custom solutions can support proprietary manufacturing methodologies, unique inventory strategies, or specialized reporting requirements that give you an edge in your market.'
    }, {
      type: 'paragraph',
      text: 'Long-term Scalability Control — Custom systems grow with your business on your terms. As you expand production capacity, add new product lines, or enter new markets, your ERP can evolve to support these changes without the limitations imposed by pre-built frameworks.'
    }, {
      type: 'subheading',
      text: 'Advantages of Custom ERP'
    }, {
      type: 'list',
      items: ['Perfect Process Alignment: Every feature serves your specific needs, eliminating the productivity drain of working around software limitations.', 'Unlimited Integration Potential: Custom systems can connect with any existing equipment, legacy systems, or third-party tools your manufacturing operation depends on.', "Ownership and Control: You own the intellectual property of your system and aren't dependent on vendor roadmaps or licensing changes.", 'Superior ROI Over Time: Despite higher upfront costs, the efficiency gains and competitive advantages often deliver significant long-term returns.']
    }, {
      type: 'subheading',
      text: 'Disadvantages of Custom ERP'
    }, {
      type: 'list',
      items: ['Higher Initial Investment: Custom development requires substantial upfront costs for design, development, testing, and deployment.', 'Extended Implementation Timeline: Building a system from scratch typically takes several months to over a year, depending on complexity.', "Ongoing Maintenance Responsibility: You'll need dedicated technical resources or vendor partnerships to maintain, update, and support your custom system.", "Development Risk: Custom projects carry the inherent risk of scope creep, timeline delays, or technical challenges that don't exist with proven solutions."]
    }, {
      type: 'heading',
      text: 'Exploring Pre-Configured ERP Solutions'
    }, {
      type: 'paragraph',
      text: 'Pre-configured ERP systems come with built-in modules, workflows, and features designed to serve common manufacturing needs. These solutions offer established functionality that can be deployed quickly with minimal customization.'
    }, {
      type: 'subheading',
      text: 'Core Strengths of Pre-Configured ERP'
    }, {
      type: 'paragraph',
      text: 'Rapid Deployment Capability — Pre-configured systems can often be implemented in weeks rather than months, allowing you to start realizing benefits immediately. This speed advantage is particularly valuable for growing businesses that need operational improvements quickly.'
    }, {
      type: 'paragraph',
      text: "Proven Functionality — These solutions have been tested across thousands of implementations, with bugs worked out and best practices built in. You benefit from the collective experience of the vendor's entire customer base."
    }, {
      type: 'paragraph',
      text: 'Cost Predictability — Subscription-based pricing models make costs predictable and typically require lower upfront investment compared to custom development projects.'
    }, {
      type: 'subheading',
      text: 'Advantages of Pre-Configured ERP'
    }, {
      type: 'list',
      items: ['Lower Initial Costs: Subscription models spread costs over time and require minimal upfront investment.', 'Established Best Practices: Built-in workflows often incorporate industry best practices that can improve your operations.', 'Vendor-Managed Updates: Software updates, security patches, and feature additions are handled by the vendor.', 'Faster Time-to-Value: Quick implementation means you start seeing operational benefits sooner.']
    }, {
      type: 'subheading',
      text: 'Disadvantages of Pre-Configured ERP'
    }, {
      type: 'list',
      items: ['Limited Customization Options: You may need to change your processes to fit the software rather than the other way around.', 'Feature Gaps: Standard solutions may lack specific functionality your manufacturing processes require.', 'Integration Limitations: Connecting with specialized equipment or legacy systems may be challenging or impossible.', 'Ongoing Subscription Costs: While initial costs are lower, long-term subscription fees can add up significantly over time.']
    }, {
      type: 'heading',
      text: 'Detailed Comparison Analysis'
    }, {
      type: 'image',
      src: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/68dad718755766f716b6f751_Screenshot%202025-09-29%20at%2011.59.25%E2%80%AFAM.png',
      alt: 'Custom ERP vs Pre-Configured ERP comparison table'
    }, {
      type: 'heading',
      text: 'Making the Right Choice for Your Manufacturing Business'
    }, {
      type: 'subheading',
      text: 'Choose Custom ERP If:'
    }, {
      type: 'paragraph',
      text: 'Your manufacturing operation involves complex, non-standard processes that require unique workflows, specialized reporting, or proprietary methodologies. Companies with complex bill of materials management, intricate batch tracking requirements, or specialized quality control processes often find pre-configured solutions inadequate.'
    }, {
      type: 'paragraph',
      text: 'You have rapid growth with evolving needs. If your business is expanding quickly and your processes are still evolving, a custom solution can adapt as you grow rather than constraining your development.'
    }, {
      type: 'paragraph',
      text: 'Integration requirements are complex. When you need to connect with specialized manufacturing equipment, legacy systems, or unique third-party tools, custom solutions provide unlimited integration possibilities.'
    }, {
      type: 'paragraph',
      text: 'You can invest in long-term competitive advantage. If ERP capabilities could differentiate your business in the marketplace, the investment in custom development may pay dividends through improved efficiency and unique capabilities.'
    }, {
      type: 'subheading',
      text: 'Choose Pre-Configured ERP If:'
    }, {
      type: 'paragraph',
      text: 'Your manufacturing processes are relatively standard and align well with common industry practices. Many discrete manufacturers with straightforward production workflows find that pre-configured solutions meet 80-90% of their needs effectively.'
    }, {
      type: 'paragraph',
      text: "Speed is critical for your business success. If you need operational improvements immediately and can't wait for custom development, pre-configured solutions deliver faster time-to-value."
    }, {
      type: 'paragraph',
      text: 'Budget constraints limit your technology investment. When upfront capital is limited, subscription-based pre-configured solutions provide access to sophisticated ERP capabilities with lower initial costs.'
    }, {
      type: 'paragraph',
      text: 'You prefer vendor-managed maintenance. If you lack internal IT resources or prefer to focus on manufacturing rather than software maintenance, pre-configured solutions remove this burden.'
    }, {
      type: 'subheading',
      text: 'The Hybrid Approach'
    }, {
      type: 'paragraph',
      text: 'Many successful manufacturing companies start with a pre-configured solution that offers extensive configuration options. This approach allows you to adjust the system within its framework while maintaining upgrade compatibility and cost efficiency. As operations grow more complex, you can evaluate transitioning to custom solutions or implementing custom modules alongside your core system.'
    }, {
      type: 'paragraph',
      text: "At Thruline we've designed our ERP to be flexible enough to match our customer's requirements, while maintaining the speed and ease of maintenance of out of the box solutions."
    }, {
      type: 'heading',
      text: 'Strategic Implementation Considerations'
    }, {
      type: 'paragraph',
      text: 'Regardless of which path you choose, successful ERP implementation requires careful planning around data migration, user training, and change management. Manufacturing businesses face unique challenges during ERP transitions, including maintaining production schedules, preserving quality control processes, and ensuring regulatory compliance throughout the implementation period.'
    }, {
      type: 'paragraph',
      text: "Consider your team's technical capabilities, as custom solutions typically require more sophisticated internal resources or stronger vendor partnerships for ongoing support. Pre-configured solutions usually come with more comprehensive user documentation and training resources."
    }, {
      type: 'heading',
      text: 'Conclusion'
    }, {
      type: 'paragraph',
      text: 'The choice between custom and pre-configured ERP solutions ultimately depends on your specific manufacturing requirements, growth trajectory, and strategic objectives. Complex, unique manufacturing processes with specialized requirements often justify custom development, while standard operations benefit from the speed and proven functionality of pre-configured solutions.'
    }, {
      type: 'paragraph',
      text: 'Remember that this decision will impact your operations for many years. Take time to thoroughly evaluate your current processes, future plans, and technical requirements before making your choice. The right ERP system becomes a foundation for growth, efficiency, and competitive advantage in your manufacturing business.'
    }, {
      type: 'paragraph',
      text: 'For manufacturing businesses ready to explore the best of both worlds, Thruline offers tailored manufacturing ERP systems that have the speed of an out of box solution, but the benefits of a custom ERP — allowing us to grow with your business requirements.'
    }],
    ctaTitle: 'Get the best of both worlds',
    ctaDescription: 'Thruline offers the speed of pre-configured solutions with the flexibility of custom ERP. See how it works for your business.',
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Talk to Our Team'
  },
  '7-mistakes-youre-making-with-erp-integrations-and-how-to-fix-them': {
    title: "7 Mistakes You're Making with ERP Integrations (and How to Fix Them)",
    date: 'September 29, 2025',
    readTime: '8 min read',
    category: 'ERP',
    author: 'Alex',
    image: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/68daefefa23275116fd19b03_7IntegrationMistakesPicture.webp',
    blocks: [{
      type: 'paragraph',
      text: "ERP integrations can make or break your manufacturing operations. When done right, they create seamless data flow between systems, automate tedious processes, and give you real-time visibility into your business. When done wrong? You're looking at budget overruns, frustrated teams, and systems that barely talk to each other."
    }, {
      type: 'paragraph',
      text: "After working with countless manufacturing companies, we've seen the same integration mistakes repeated over and over. The good news? These issues are totally preventable if you know what to watch out for."
    }, {
      type: 'heading',
      text: 'Mistake #1: Over-Customizing Your ERP System'
    }, {
      type: 'paragraph',
      text: "Here's the trap: Your old system worked a certain way, so naturally you want your new ERP to match those exact workflows. This leads to endless customization requests that can increase implementation costs by 50% or more."
    }, {
      type: 'paragraph',
      text: "Every custom field, modified workflow, and unique report adds complexity. Your system becomes harder to maintain, updates become nightmares, and you're essentially paying premium prices for a solution that's been stripped of its out-of-the-box benefits."
    }, {
      type: 'paragraph',
      text: 'How to fix it: Challenge every customization request. Ask "Do we really need this, or can we adapt our process to match industry best practices?" Most ERP systems are built on years of manufacturing expertise — trust that workflow. Only customize when it\'s absolutely critical to your core business operations.'
    }, {
      type: 'heading',
      text: 'Mistake #2: Skipping Comprehensive Testing'
    }, {
      type: 'paragraph',
      text: "Testing often gets squeezed when timelines get tight. Teams run a few quick checks, everything seems to work, and they call it good. Then go-live happens and suddenly data isn't syncing properly between your inventory management and accounting systems."
    }, {
      type: 'paragraph',
      text: 'Poor testing is behind most post-implementation headaches. You might catch obvious errors, but miss subtle issues that only show up under real-world conditions with actual data volumes and user behaviors.'
    }, {
      type: 'paragraph',
      text: "How to fix it: Plan for thorough end-to-end testing that covers every integration touchpoint. Test with realistic data volumes, not just a few sample records. Include load testing to make sure your integrations can handle peak usage periods. Yes, it takes time upfront, but it's nothing compared to fixing issues after your team is already depending on the system."
    }, {
      type: 'heading',
      text: 'Mistake #3: Ignoring Data Quality Before Migration'
    }, {
      type: 'paragraph',
      text: "Garbage in, garbage out. If your existing data has inconsistencies, duplicates, or missing information, those problems will multiply across all your integrated systems. We've seen companies struggle for months with unreliable reports and inventory discrepancies simply because they migrated dirty data."
    }, {
      type: 'paragraph',
      text: 'Data quality issues become exponentially worse in integrated environments because bad data propagates across multiple systems automatically. One incorrect part number can mess up inventory counts, purchasing decisions, and production planning simultaneously.'
    }, {
      type: 'paragraph',
      text: "How to fix it: Audit your data before any integration work begins. Set up data governance policies that define standards for accuracy, completeness, and consistency. Use data cleansing tools to identify and fix duplicates, missing fields, and formatting inconsistencies. It's tedious work, but clean data is the foundation of reliable integrations."
    }, {
      type: 'heading',
      text: 'Mistake #4: Lacking Technical Integration Expertise'
    }, {
      type: 'paragraph',
      text: "ERP integration isn't just about connecting System A to System B. It requires understanding data formats, API limitations, security requirements, error handling, and ongoing maintenance needs. Many companies underestimate the technical complexity and try to handle integrations with general IT staff who lack specialized knowledge."
    }, {
      type: 'paragraph',
      text: 'Without proper expertise, integration projects take longer, cost more, and often result in fragile connections that break under normal business conditions. You end up with workarounds and manual processes that defeat the whole purpose of integration.'
    }, {
      type: 'paragraph',
      text: "How to fix it: Be honest about your team's integration capabilities. If you don't have dedicated integration specialists, partner with vendors or consultants who do. Look for teams that have specific experience with your ERP platform and the third-party systems you need to connect. The investment in proper expertise pays for itself through faster implementation and more reliable results."
    }, {
      type: 'heading',
      text: 'Mistake #5: Poor Project Planning and Timeline Management'
    }, {
      type: 'paragraph',
      text: 'Integration projects have a nasty habit of expanding beyond their original scope. What starts as "connect our ERP to our CRM" somehow becomes "also integrate our shipping software, update our reporting tools, and add mobile access for the warehouse team."'
    }, {
      type: 'paragraph',
      text: 'Scope creep kills timelines and budgets. Meanwhile, unrealistic initial timelines create pressure to cut corners on testing, training, and proper implementation procedures.'
    }, {
      type: 'paragraph',
      text: "How to fix it: Define your integration scope clearly and stick to it. Create detailed project timelines with realistic milestones and buffer time for unexpected issues. Track progress consistently and be prepared to adjust timelines rather than rushing through critical phases. Remember: it's better to deliver a solid integration a few weeks late than to go live with a broken system on schedule."
    }, {
      type: 'heading',
      text: 'Mistake #6: Creating Data Silos Instead of Breaking Them Down'
    }, {
      type: 'paragraph',
      text: 'The whole point of ERP integration is to break down data silos and create unified information flow. But poorly planned integrations can actually make the silo problem worse by creating complex, hard-to-maintain connections between systems.'
    }, {
      type: 'paragraph',
      text: 'When different departments manage their own integration projects independently, you end up with a web of point-to-point connections that are difficult to monitor and maintain. Data flows in circles, gets duplicated across systems, and becomes impossible to trace when issues arise.'
    }, {
      type: 'paragraph',
      text: 'How to fix it: Take a holistic approach to integration planning. Map out all your data flows before building any connections. Consider using an integration platform that can manage multiple connections through a central hub rather than creating dozens of individual point-to-point integrations. Encourage cross-department collaboration to ensure data flows serve the entire organization, not just individual teams.'
    }, {
      type: 'heading',
      text: 'Mistake #7: Treating Integration as a One-Time Project'
    }, {
      type: 'paragraph',
      text: "Here's a reality check: integration isn't something you do once and forget about. Systems get updated, business requirements change, and new applications get added to your tech stack. An integration that works perfectly today might break next month when your accounting software releases a new version."
    }, {
      type: 'paragraph',
      text: "Many companies treat integration like a construction project: build it once and walk away. Then they're surprised when connections start failing or when they need to add new systems to the mix."
    }, {
      type: 'paragraph',
      text: "How to fix it: Plan for ongoing maintenance and monitoring from day one. Set up automated alerts for integration failures. Document all your connections thoroughly so future changes can be implemented smoothly. Budget for regular maintenance and updates — it's part of the cost of doing business with integrated systems."
    }, {
      type: 'heading',
      text: 'Making Integration Work for Your Business'
    }, {
      type: 'paragraph',
      text: "ERP integration doesn't have to be painful. When you avoid these common mistakes, you end up with systems that actually work together, data you can trust, and processes that run smoothly without constant manual intervention."
    }, {
      type: 'paragraph',
      text: "The key is approaching integration as a strategic business initiative, not just a technical project. Focus on solving real business problems, plan thoroughly, and don't skip the foundational work of clean data and proper testing."
    }, {
      type: 'paragraph',
      text: 'Your manufacturing operations depend on accurate, timely information flowing between systems. Get the integration right, and everything else becomes easier.'
    }],
    ctaTitle: 'Get your integrations right the first time',
    ctaDescription: 'Thruline handles ERP integrations as part of every implementation — no fragile connections or manual workarounds.',
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Talk to Our Team'
  },
  'what-it-takes-to-do-a-60-day-erp-implementation': {
    title: 'What it Takes to Do a 60-Day ERP Implementation',
    date: 'December 2, 2025',
    readTime: '8 min read',
    category: 'ERP',
    author: 'Alex',
    image: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/692e78b9f019f5dc0ec3c3f4_iyIA9nMudnm.webp',
    blocks: [{
      type: 'paragraph',
      text: 'Remember when getting an ERP system up and running felt like planning a moon landing? Six months minimum, sometimes stretching into a full year, with enough project meetings to fill a small conference room. Well, those days are officially behind us — and the business world has taken notice.'
    }, {
      type: 'paragraph',
      text: "Suddenly, everyone's buzzing about 60-day ERP implementations. Your competitors are talking about it, industry publications are covering it, and your CFO is probably asking why your last system took eight months to deploy. But here's the thing: this isn't just another tech trend that'll disappear next quarter. The 60-day implementation timeline represents a fundamental shift in how smart companies approach enterprise software, and there are some pretty compelling reasons why you should pay attention."
    }, {
      type: 'heading',
      text: 'The Speed Revolution Actually Makes Sense'
    }, {
      type: 'paragraph',
      text: 'Let\'s be honest: traditional ERP implementations have been painfully slow for way too long. Three to six months used to be considered "fast," and most projects dragged on even longer than that. But when you really think about it, why should implementing business software take half a year?'
    }, {
      type: 'paragraph',
      text: "The answer is that it shouldn't, and finally, the technology and methodologies have caught up to match what businesses actually need. A 60-day timeline isn't just about moving faster: it's about recognizing that lengthy implementations create their own problems. The longer your implementation drags on, the more likely you are to encounter scope creep, team burnout, and that dreaded scenario where your business requirements change halfway through the project."
    }, {
      type: 'image',
      src: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/692e7858b20c2cadb7dabf70_ioPVYbUsXeB.webp',
      alt: 'ERP implementation timeline comparison'
    }, {
      type: 'paragraph',
      text: "Plus, let's talk about the elephant in the room: cash flow. Every day your new ERP system isn't live is another day you're not seeing returns on your investment. Meanwhile, you're still paying for implementation resources, project management, and probably keeping your old system running in parallel. The financial case for speed isn't just compelling: it's obvious."
    }, {
      type: 'heading',
      text: 'How 60-Day Implementations Actually Work'
    }, {
      type: 'paragraph',
      text: "Here's where it gets interesting. The companies successfully hitting 60-day timelines aren't just working faster: they're working smarter. The secret sauce lies in what the industry calls \"implementation accelerators,\" and they're completely changing the game."
    }, {
      type: 'paragraph',
      text: 'Think of accelerators as the difference between building a house from scratch versus using prefab components. Instead of mapping every single business process from zero, configuring every possible setting, and testing every conceivable scenario, these accelerators provide proven frameworks that you can customize for your specific needs.'
    }, {
      type: 'paragraph',
      text: 'The three areas where accelerators deliver the biggest time savings are pretty revealing:'
    }, {
      type: 'list',
      items: ['Mapping phases: Instead of documenting your processes from a blank slate, you start with proven templates', 'Documentation phases: Pre-built documentation frameworks that you adapt rather than create', 'Testing phases: Standardized test scripts and scenarios that cover the most common use cases']
    }, {
      type: 'paragraph',
      text: "One manufacturing company — a greenfield fertilizer operation with no legacy baggage — managed to implement their entire manufacturing ERP system in under six months using these accelerator approaches. And that was a few years ago when the tools weren't nearly as refined as they are today."
    }, {
      type: 'heading',
      text: '60-Day Modern ERP vs. 12+ Month Legacy ERP: A Quick Comparison'
    }, {
      type: 'paragraph',
      text: "Still picturing a year-long slog? Here's the side-by-side, no fluff:"
    }, {
      type: 'paragraph',
      text: 'Traditional 12-month pain points:'
    }, {
      type: 'list',
      items: ['Heavy customization that snowballs into scope creep and rewrites', 'Drawn-out phases with idle time, handoffs, and expensive rework', 'High consulting costs and endless change orders', 'Business disruption: dual systems, meeting fatigue, and slipping timelines']
    }, {
      type: 'paragraph',
      text: 'Modern 60-day benefits:'
    }, {
      type: 'list',
      items: ['Rapid onboarding with role-based training inside the live system', 'Fixed pricing and a bounded scope that reduce risk', 'Immediate ROI: earlier visibility, automation, and cash-flow impact', 'Faster feedback loops — iterate in production, not in a slide deck']
    }, {
      type: 'paragraph',
      text: "Bottom line: speed isn't a gimmick — it's a risk strategy. That's why Thruline is built around fixed-price, 60-day delivery that maps to your actual workflow."
    }, {
      type: 'heading',
      text: "The Business Case That's Hard to Ignore"
    }, {
      type: 'paragraph',
      text: "Let's cut through the marketing speak and talk real numbers. When your ERP implementation timeline drops from six months to two, you're looking at some serious financial benefits that go way beyond just saving on consulting fees."
    }, {
      type: 'paragraph',
      text: "First, there's the obvious cost reduction. Shorter implementations mean fewer billable hours, less project management overhead, and reduced internal resource allocation. Your team can get back to their day jobs instead of spending months in implementation meetings."
    }, {
      type: 'paragraph',
      text: "But the real money is in the accelerated time-to-value. Every week you shave off your implementation timeline is another week you're getting real business benefits from your new system. Whether that's automated inventory management, streamlined financial processes, or better customer order tracking, you're seeing returns faster."
    }, {
      type: 'image',
      src: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/692e7858b20c2cadb7dabf76_Tt4LUcy1oGK.webp',
      alt: 'Business case for rapid ERP implementation'
    }, {
      type: 'paragraph',
      text: "Here's something most people don't consider: faster implementations also mean less disruption to your business operations. Instead of having your team in \"implementation mode\" for half a year, you're minimizing the productivity dip that always comes with major system changes."
    }, {
      type: 'heading',
      text: 'Real-World Proof Points'
    }, {
      type: 'paragraph',
      text: 'The best part about the 60-day implementation conversation is that it\'s backed by actual results, not just vendor promises. We\'re seeing case studies across different industries showing companies achieving "cost reduction and efficiency in 60 days" with modern ERP platforms.'
    }, {
      type: 'paragraph',
      text: "What's particularly encouraging is that these success stories aren't coming from tiny companies with simple processes. Mid-market manufacturers with complex operations, multi-location businesses, and companies with specific compliance requirements are all hitting these accelerated timelines."
    }, {
      type: 'paragraph',
      text: "The common thread? They're choosing ERP solutions designed for rapid deployment rather than trying to force traditional, heavyweight systems into faster timelines. It's like the difference between building a custom sports car from scratch versus buying one that's already engineered for performance."
    }, {
      type: 'heading',
      text: 'What This Means for Your Business'
    }, {
      type: 'paragraph',
      text: 'So why should you care about 60-day implementations? Because the companies embracing this approach are gaining competitive advantages that compound over time.'
    }, {
      type: 'paragraph',
      text: "Think about it: while your competitors are still six months into their implementation projects, you're already optimizing your newly deployed system and seeing real business benefits. You're capturing efficiency gains, improving customer service, and making data-driven decisions while they're still in testing phases."
    }, {
      type: 'image',
      src: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/692e7858b20c2cadb7dabf73_VsXSjqX3Idt.webp',
      alt: 'Competitive advantage of fast ERP deployment'
    }, {
      type: 'paragraph',
      text: "But here's the most important part: 60-day implementations are only possible when you choose the right ERP solution from the start. Not every system is designed for rapid deployment, and trying to rush a complex, heavyweight platform is a recipe for disaster."
    }, {
      type: 'paragraph',
      text: "The key is finding an ERP solution that's built for your specific business needs without unnecessary complexity. Custom doesn't have to mean complicated, and fast doesn't have to mean generic."
    }, {
      type: 'heading',
      text: 'The Reality Check'
    }, {
      type: 'paragraph',
      text: "Now, before you get too excited about 60-day timelines, let's be real about what makes this possible. Speed comes from preparation, the right tools, and — most importantly — choosing an ERP solution that's designed for your business from day one."
    }, {
      type: 'paragraph',
      text: "If you're trying to customize a one-size-fits-all solution to fit your unique processes, you're going to hit roadblocks that slow everything down. But if you start with a system that's built for your industry and business model, those 60 days become not just possible, but realistic."
    }, {
      type: 'paragraph',
      text: "The companies hitting these timelines aren't cutting corners: they're being smarter about their approach. They're choosing solutions that offer the customization they need without the complexity they don't."
    }, {
      type: 'heading',
      text: 'Making the Smart Move'
    }, {
      type: 'paragraph',
      text: "The 60-day ERP implementation trend isn't just about speed for speed's sake. It's about recognizing that in today's business environment, agility matters more than ever. Your ERP system should be an accelerator for your business, not a project that drags on for months."
    }, {
      type: 'paragraph',
      text: "If you're considering an ERP upgrade or implementation, the question isn't whether 60-day timelines are possible: it's whether you're choosing a solution that makes them achievable. Because in a world where your competitors are getting their systems live in two months, can you really afford to spend six?"
    }, {
      type: 'paragraph',
      text: "The conversation around 60-day implementations is really about a fundamental shift in expectations. We're finally at a point where enterprise software can be both powerful and fast to deploy, both customized and standardized, both comprehensive and streamlined."
    }, {
      type: 'paragraph',
      text: "And honestly? It's about time."
    }, {
      type: 'paragraph',
      text: "Ready to explore what a 60-day implementation could look like for your business? The companies making this timeline work aren't just getting lucky: they're making smart choices about their ERP solutions from the very beginning."
    }],
    ctaTitle: 'See what 60 days looks like for your business',
    ctaDescription: 'Learn more about how Thruline approaches rapid ERP deployment and see if your timeline expectations need an upgrade too.',
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Talk to Our Team'
  },
  'struggling-with-inventory-chaos-7-signs-your-manufacturing-business-needs-better-inventory-management-software': {
    title: '7 Signs Your Manufacturing Business Needs Better Inventory Management Software',
    date: 'October 4, 2025',
    readTime: '9 min read',
    category: 'Inventory',
    author: 'Alex',
    image: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/68e15fcd8ae5b2178127b8cb_LL7OxqSoHbt.webp',
    blocks: [{
      type: 'paragraph',
      text: "Let's be honest, if you're reading this, there's a good chance your inventory management feels like herding cats. You know the feeling: that sinking sensation when a critical production run gets delayed because nobody knew you were out of a key component."
    }, {
      type: 'paragraph',
      text: "Here's the thing: inventory chaos isn't just inconvenient, it's expensive. Really expensive. We're talking about tied-up capital, missed deadlines, frustrated customers, and the kind of stress that makes you question your career choices at 2 AM."
    }, {
      type: 'paragraph',
      text: 'But how do you know when your inventory management has crossed the line from "quirky" to "completely dysfunctional"? Let\'s dive into the seven telltale signs that it\'s time to ditch the spreadsheets and embrace modern inventory management software.'
    }, {
      type: 'heading',
      text: '1. Your Warehouse Looks Like a Storage Wars Episode'
    }, {
      type: 'paragraph',
      text: "Walk through your facility right now. Go ahead, I'll wait."
    }, {
      type: 'paragraph',
      text: "Do you see stacks of materials that have been gathering dust longer than some of your employees have been with the company? Are there products from 2022 still taking up prime real estate while you're scrambling for space to store new inventory?"
    }, {
      type: 'image',
      src: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/68e1604decc054b681f6d66d_89HdGR51jJZ.webp',
      alt: 'Cluttered warehouse inventory'
    }, {
      type: 'paragraph',
      text: "Excessive stock accumulation isn't just a space problem, it's a cash flow nightmare. Every item sitting idle in your warehouse represents money that could be working elsewhere in your business."
    }, {
      type: 'paragraph',
      text: "The real kicker? You're probably still ordering more of the same items because nobody has a clear picture of what you actually have on hand."
    }, {
      type: 'heading',
      text: '2. Your Inventory Turns Slower Than a Government Bureaucracy'
    }, {
      type: 'paragraph',
      text: "When products sit in your inventory longer than a Netflix series takes to get renewed for a second season, you've got a problem. Slow inventory turnover is like having money locked in a safe that you never open."
    }, {
      type: 'paragraph',
      text: "Fast-moving inventory is healthy inventory. It means your production planning aligns with actual demand, your sales team knows what they're working with, and your cash isn't trapped in materials nobody wants."
    }, {
      type: 'heading',
      text: "3. You're Still Living in the Paper Age"
    }, {
      type: 'paragraph',
      text: 'If your inventory management system consists primarily of clipboards, Post-it notes, and the institutional knowledge of "Gary from receiving" who\'s been here since the Clinton administration, we need to talk.'
    }, {
      type: 'paragraph',
      text: "Paper-based systems don't just slow you down: they actively sabotage your accuracy. Human error in data entry is inevitable, and when those errors compound across multiple transactions, you end up with inventory records that bear about as much resemblance to reality as a Hollywood action movie."
    }, {
      type: 'heading',
      text: '4. Your Production Schedule Is More Like a Production "Suggestion"'
    }, {
      type: 'paragraph',
      text: "Nothing ruins a perfectly good Monday morning like discovering that the critical component you need for this week's big order is mysteriously missing from your inventory. Again."
    }, {
      type: 'paragraph',
      text: "When your production deadlines feel more like wishful thinking than actual commitments, it's usually because your inventory management can't keep up with your operational needs."
    }, {
      type: 'image',
      src: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/68e1604decc054b681f6d670_LXKqyu2tQV5.webp',
      alt: 'Production scheduling challenges'
    }, {
      type: 'heading',
      text: '5. "Real-Time" Means "I\'ll Check Tomorrow"'
    }, {
      type: 'paragraph',
      text: "Quick question: What's your current inventory level for your top five components? Don't know off the top of your head? Need to go check three different systems, call someone, or physically walk to the warehouse?"
    }, {
      type: 'paragraph',
      text: "In today's manufacturing environment, \"I'll get back to you\" isn't fast enough. Your customers expect immediate answers about availability, your production team needs instant access to material status, and your purchasing department requires up-to-the-minute data."
    }, {
      type: 'heading',
      text: '6. Your Suppliers Feel Like Distant Acquaintances'
    }, {
      type: 'paragraph',
      text: "Supplier collaboration shouldn't feel like trying to coordinate a potluck dinner through smoke signals. Yet many manufacturers struggle with basic communication about incoming materials, delivery schedules, and inventory replenishment needs."
    }, {
      type: 'paragraph',
      text: "Poor supplier integration leads to coordination problems that ripple through your entire operation. You get surprise deliveries when your warehouse is full, stockouts when you thought materials were coming, and constant back-and-forth communications that waste everyone's time."
    }, {
      type: 'heading',
      text: '7. Your Business Decisions Are Based on Hunches and Hope'
    }, {
      type: 'paragraph',
      text: "Making strategic business decisions without solid inventory analytics is like trying to navigate by the stars when you're colorblind. You might eventually get where you're going, but you're definitely taking the scenic route."
    }, {
      type: 'paragraph',
      text: "Modern inventory management isn't just about tracking what you have: it's about understanding patterns, predicting needs, and optimizing your inventory mix based on actual data rather than gut feelings."
    }, {
      type: 'heading',
      text: 'The Path Forward: From Chaos to Control'
    }, {
      type: 'paragraph',
      text: 'Recognizing these signs is the first step toward transforming your inventory management from a daily headache into a competitive advantage. Modern inventory management software addresses each of these pain points systematically.'
    }, {
      type: 'image',
      src: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/68e1604eecc054b681f6d675_9pCKnhKSlZG.webp',
      alt: 'Modern inventory management dashboard'
    }, {
      type: 'paragraph',
      text: "The manufacturers who thrive in today's competitive environment are the ones who've moved beyond treating inventory as a necessary evil and started leveraging it as a strategic asset."
    }, {
      type: 'paragraph',
      text: "Your inventory doesn't have to be chaotic. With the right approach and tools, it can become one of your strongest competitive advantages. The question isn't whether you can afford to upgrade your inventory management: it's whether you can afford not to."
    }],
    ctaTitle: 'Take control of your inventory',
    ctaDescription: 'See how Thruline can transform your inventory management from chaos to competitive advantage.',
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Talk to Our Team'
  },
  'how-manufacturing-methodologies-shape-erp-system-needs': {
    title: 'How Manufacturing Methodologies Shape ERP System Needs',
    date: 'October 15, 2024',
    readTime: '5 min read',
    category: 'ERP',
    author: 'Alex',
    image: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/67b7633f7872d71da442636e_66fc71998d968d8119460b67-homa-appliances-pWUyHVJgLhg-unsplash.jpg',
    blocks: [{
      type: 'paragraph',
      text: 'The way a manufacturer operates impacts the effectiveness of its ERP system. While two companies might produce similar products, the processes behind their operations can vary significantly, leading to different needs from their ERP solutions. Take, for example, a company producing mass-market clothing compared to another creating bespoke, tailored suits. Although both are in the same industry, their production approaches require different ERP capabilities to ensure efficient planning and resource management.'
    }, {
      type: 'paragraph',
      text: 'For manufacturers that follow a Make to Stock (MTS) model, the focus is on anticipating demand and having products ready for customers. In this case, an ERP system must help maintain the right balance of inventory through demand forecasting and real-time tracking, ensuring production schedules align with market needs without overproducing. The ability to manage changes in product designs seamlessly through engineering change orders is also vital in keeping inventory relevant.'
    }, {
      type: 'paragraph',
      text: 'In contrast, companies that operate on a Make to Order (MTO) basis cater to more specific customer demands, where products are made only after an order is placed. For these manufacturers, an ERP system that can handle accurate job costing, estimating, and provide clear communication with vendors is essential. Production timelines must be carefully managed to ensure that custom products are delivered on time, while maintaining high standards of quality and customization. This model becomes even more complex with approaches like Assemble to Order (ATO) and Engineer to Order (ETO), where customer requirements drive not only production but the design itself. Here, ERP systems need to be flexible enough to integrate design tools and manage complex bill of materials.'
    }, {
      type: 'paragraph',
      text: 'When it comes to Lean Manufacturing, where the goal is to eliminate waste and streamline processes, ERP systems play a critical role in supporting just-in-time production methods. By integrating lean principles, such as continuous improvement and waste reduction, the system helps businesses stay efficient and focused on creating value with minimal delays. Tools that track workflows and optimize production setups contribute to a smooth, waste-free operation.'
    }, {
      type: 'paragraph',
      text: "Ultimately, aligning your ERP system with the unique demands of your manufacturing methodology can make the difference between a disjointed operation and a well-oiled machine. Whether you're responding to market demand, customizing orders, or reducing inefficiencies, the right ERP system should feel like an extension of your production philosophy, supporting your goals every step of the way."
    }, {
      type: 'paragraph',
      text: 'Thruline simplifies and unifies your manufacturing operations with tools that cover every stage of production. From planning and scheduling to production tracking and costing, the platform supports a range of manufacturing processes, ensuring that your business operates smoothly and efficiently. The built-in automation and workflow features allow your team to focus on high-value tasks, reducing manual effort and minimizing errors.'
    }, {
      type: 'paragraph',
      text: "Whether you need to manage complex bills of materials (BOM), optimize capacity planning, or streamline your sales and inventory management, Thruline offers a flexible, all-in-one solution. The system's project-specific manufacturing features are ideal for companies that need to track labor and materials across multiple projects while ensuring costs stay in check."
    }, {
      type: 'paragraph',
      text: "Finding the right ERP system for your manufacturing business is a critical decision. You need a platform that not only meets today's demands but also grows with your company. Thruline offers comprehensive solutions tailored to your specific needs. Unlike many ERP systems that require separate modules for different business areas, Thruline integrates everything from project management to procedures, making it a flexible and scalable solution for companies of all sizes."
    }, {
      type: 'paragraph',
      text: "Thruline's emphasis on modern technology and ease of integration ensures that your business stays ahead of the curve, whether you're dealing with complex production schedules, evolving customer demands, or new market opportunities. By centralizing data and processes, Thruline enables your team to work smarter, faster, and more effectively, giving your business the competitive edge it needs to thrive."
    }, {
      type: 'paragraph',
      text: "At Thruline we're not just built to manage your projects — we're built to manage your business, which is why we've tackled the hardest part about business software, making sure you can use all of your company's data from a single place."
    }],
    ctaTitle: 'Find the right ERP for your methodology',
    ctaDescription: 'Reach out for a private consultation to see how Thruline aligns with your manufacturing approach.',
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Book a Consultation'
  },
  'erp-is-dead-long-liver-erp': {
    title: 'ERP is Dead - Long Live ERP',
    date: 'October 15, 2024',
    readTime: '4 min read',
    category: 'ERP',
    author: 'Alex',
    image: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/67b762d01230118dd91dedf8_erp_tombstone.jpg',
    blocks: [{
      type: 'paragraph',
      text: '"The reports of my death are greatly exaggerated" — ERP, probably'
    }, {
      type: 'paragraph',
      text: 'This year there have been two massive fundings in the ERP world. $500 million for Cin7, and another $500 million for "upstart" Odoo. Cin7 is about 11 years old while Odoo is over 20 years old.'
    }, {
      type: 'paragraph',
      text: 'From Odoo\'s funding announcement: Andrew Reed, partner at Sequoia Capital, added that the market Odoo is addressing "just requires more gestation time than most startups both because the core system is very complex, and making it simple to use for small businesses and various countries is no small feat."'
    }, {
      type: 'paragraph',
      text: "There's been a theory that ERP is going the way of the dinosaur, to be replaced by a frankenstein of SaaS solutions. Expense tools such as Expensify combined with punch out systems like Coupa would be used by pure accounting systems like Quickbooks to provide an ever more interconnected suite of apps that would be cheaper, more flexible, and have just as much functionality as an SAP. Augmenting these applications, data warehouses such as Snowflake and Domo would provide analytic tools that would replace the data analysis functionality of an ERP. Why install an SAP when you could generate the same reports and provide the same functionality for cheaper over several applications? With these fundings, this theory doesn't seem to be panning out."
    }, {
      type: 'paragraph',
      text: "It turns out tying a handful of applications together is more difficult than it sounds. At Thruline we've seen multiple attempts to integrate accounting systems such as Quickbooks into several other applications. While these integrations work while a business is small (< 5 million in revenue), the increasing complexity of integrating multiple pieces of software becomes much too burdensome as a company grows, leading to an unmanageable web of applications. At that point, businesses overwhelmingly migrate to an ERP, like Odoo. The power of an ERP comes from the fact that the product suite is greater than the sum of its parts. There is value in having all of a company's processes in one place — even if each individual solution may not be considered the best in the industry."
    }, {
      type: 'paragraph',
      text: "The advent of AI has called up a new generation of ERP focused startups, such as Cognizant, Doss and yes — Thruline. These startups are building a new generation of ERP that delivers the full product suite (inventory, accounting, sales, purchasing) at the earliest stages of a company's lifecycle. By leveraging newer solutions, these startups can generate customized workflows designed to match a company's business from day one — replacing the web of SaaS software holding companies back."
    }, {
      type: 'paragraph',
      text: "It's an exciting time for ERP — the industry seems to be backing ERP's promise of an all in one solution, and newer players are entering to disrupt the market. Seems like ERP isn't dead after all."
    }],
    ctaTitle: 'See the next generation of ERP',
    ctaDescription: 'Thruline is building a new kind of ERP for manufacturers — one that works from day one.',
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Talk to Our Team'
  },
  'how-companies-handle-engineer-purchase-requests-three-approaches-to-procurement': {
    title: 'How Companies Handle Engineer Purchase Requests: Three Approaches to Procurement',
    date: 'May 4, 2025',
    readTime: '5 min read',
    category: 'ERP',
    author: 'Alex',
    image: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/6817af69c7cc8e82d7e50670_ChatGPT%20Image%20May%203%2C%202025%2C%2009_51_59%20AM.jpg',
    blocks: [{
      type: 'paragraph',
      text: "In the fast-paced world of engineering, efficient procurement processes can make or break project timelines. Companies have developed various approaches to handle purchase requests from their engineering teams, balancing cost control with technical needs. Let's explore the three main organizational structures, the typical approval process, and how accounting teams manage these transactions."
    }, {
      type: 'subheading',
      text: 'Engineers as Buyers'
    }, {
      type: 'paragraph',
      text: 'In smaller organizations or specialized technical fields, engineers often communicate directly with vendors. This approach recognizes that engineers understand technical specifications best and can resolve issues without non-technical staff filtering information. Companies using this model typically:'
    }, {
      type: 'list',
      items: ['Allow engineers to select vendors and negotiate technical requirements', 'Require copying purchasing on communications to maintain oversight', 'Trust engineers to be cost-conscious while prioritizing technical needs', 'Work well for highly specialized equipment where technical expertise is crucial']
    }, {
      type: 'paragraph',
      text: 'When engineers serve as their own buyers there are typically a lot of purchases on credit cards, and generally POs are created only for vendors that require them. If teams are not proactive in tagging their expenses, there can be a lot of work come month end close for accounting teams as they track down the correct cost categorizations.'
    }, {
      type: 'subheading',
      text: 'One Buyer for the Company'
    }, {
      type: 'paragraph',
      text: 'Mid-sized companies often centralize purchasing with a single procurement specialist who handles all engineering requests. This model:'
    }, {
      type: 'list',
      items: ['Creates a streamlined communication channel with vendors', 'Allows for consistent negotiation tactics and relationship building', 'Enables volume discounts through consolidated purchasing', 'May introduce delays when technical specifications require clarification']
    }, {
      type: 'paragraph',
      text: "Once your company reaches 10 or more employees, it can be beneficial to consolidate procurement. Your buyer can not only streamline the purchasing process, but also make it easier for your engineers by removing the friction of purchasing. This can free up your team's time to focus on engineering problems. A dedicated buyer also manages your internal procurement software, which helps alleviate accounting and organization reporting issues."
    }, {
      type: 'subheading',
      text: 'Fully Staffed Procurement Team'
    }, {
      type: 'paragraph',
      text: 'Larger organizations implement comprehensive procurement departments with dedicated specialists. These teams:'
    }, {
      type: 'list',
      items: ['Include procurement engineers who bridge technical and purchasing knowledge', 'Manage complex supplier relationships and contracts', 'Implement strategic sourcing practices across multiple projects', 'Create standardized processes for all purchasing activities']
    }, {
      type: 'paragraph',
      text: "Large organizations maintain a fully staffed procurement team to handle all of the complexity that comes with a large company. It's important to maintain a balance between speed and organization, as too many procurement rules can slow down progress and add too much red tape and paperwork to the rest of the organization."
    }, {
      type: 'subheading',
      text: 'Generic Approval Process for Purchase Requests'
    }, {
      type: 'paragraph',
      text: 'Regardless of company structure, most purchase request processes follow these key steps:'
    }, {
      type: 'list',
      items: ['Needs Identification: An engineer identifies a requirement for goods or services', 'Purchase Requisition Creation: A formal request is submitted with detailed specifications and justification', 'Review and Approval: Requests route through appropriate managers based on amount, department, and type of purchase', 'Supplier Selection: Identifying suitable vendors, often requiring multiple quotes for comparison', 'Final Approval: Higher-level sign-off for budget allocation and compliance verification', 'Purchase Order Creation: A formal PO is generated and sent to the selected vendor']
    }, {
      type: 'paragraph',
      text: "The most effective companies understand that procurement isn't just about controlling costs — it's about enabling engineers to access the resources they need efficiently. The right balance between technical oversight and procurement expertise creates a system where projects move forward smoothly while maintaining financial discipline."
    }, {
      type: 'paragraph',
      text: "We've created Thruline with this in mind — supporting your company's journey as you grow from a team of engineers into a large company. We're with you every step of the way to make sure your operations run smoothly and support any procurement workflow you need!"
    }],
    ctaTitle: 'Streamline your procurement workflow',
    ctaDescription: "Whether you're a team of engineers or a fully staffed procurement department, Thruline grows with you.",
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Talk to Our Team'
  },
  'inventory-management-what-do-i-have': {
    title: 'Inventory Management: What do I have?',
    date: 'October 15, 2024',
    readTime: '6 min read',
    category: 'Inventory',
    author: 'Alex',
    image: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/67b764060b01c8ace0a0e09c_66623070e569a0ec2951b6b4-ruchindra-gunasekara-GK8x_XCcDZg-unsplash.jpg',
    blocks: [{
      type: 'paragraph',
      text: 'The first question that an inventory management system (sometimes known as a warehouse management system or WMS) has to answer is: What inventory do I have?'
    }, {
      type: 'paragraph',
      text: 'Answering what inventory do I have is made up of a few pieces of information:'
    }, {
      type: 'ordered-list',
      items: ['What is the list of parts that I am tracking', 'Where are the physical locations of these parts', 'How much of each part do I have']
    }, {
      type: 'subheading',
      text: 'What is the list of parts that I am tracking'
    }, {
      type: 'paragraph',
      text: "When listing out the parts that you are tracking, most companies need to maintain 2 lists of part numbers: the part numbers they purchase from vendors and their own internal part number. For example, if we're running a coffee shop we may purchase mugs from several different places. However, for our internal tracking, we may consider all mugs the same."
    }, {
      type: 'paragraph',
      text: 'Vendor Part Numbers: Amazon 8oz Mug, Amazon 12oz Mug, Walmart 8oz Mug, Walmart 12oz Mug.'
    }, {
      type: 'paragraph',
      text: 'Internal Part Numbers: 8oz Mug, 12oz Mug.'
    }, {
      type: 'paragraph',
      text: 'When we receive inventory we need to automatically add in inventory that matches our internal part numbers.'
    }, {
      type: 'subheading',
      text: 'Where are the physical locations of these parts'
    }, {
      type: 'paragraph',
      text: "This is where we want to track physical locations. Most companies will only need to track their internal locations, i.e. where in the warehouse their parts are located. Rule of thumb is to start simple, usually with a single location for your warehouse. As it expands, and you keep more parts in house, you'll want to expand sub locations — such as Shelf A, Shelf B, etc."
    }, {
      type: 'paragraph',
      text: 'If you are tracking information from 3PLs (3rd party logistic platforms) it is recommended you combine their daily reports into your inventory system. This will give you the ability to see where all of your parts are in a single glance.'
    }, {
      type: 'subheading',
      text: 'How much of each part do I have'
    }, {
      type: 'paragraph',
      text: "The two important pieces of tracking your quantity of parts are unit of measure and lot/serial number tracking. Units of measure are extremely important to maintain a clean record of, as you will need to convert different units of measure as your company scales. You may buy lbs of coffee beans, but only use ounces to measure the beans you use. You'll want to keep these measures separate to keep an accurate history and tracking of what your usage is."
    }, {
      type: 'paragraph',
      text: "Secondly, if you track lot or serial numbers you'll need to maintain an accurate record of these. Lot and serial numbers help you track information about a certain instance of an inventory quantity you received. Let's say you received 2lbs of Kirkland coffee beans on Monday and 1lb of Kirkland coffee beans on Friday. Your inventory warehouse may show 3lbs of Kirkland coffee beans, but that wouldn't tell you the age of each lb. Since coffee beans have a shelf life, we need to track the oldest beans we've received, so we can use them first."
    }, {
      type: 'paragraph',
      text: "Enter lots — when we receive coffee beans we can assign a number, let's say B0001, to the receipt. This lot number stores additional information, in this case the day it was received. In this example our store room wouldn't showcase 3lbs of Kirkland coffee beans, it would show:"
    }, {
      type: 'paragraph',
      text: '2lb - Kirkland Coffee - B0001\n1lb - Kirkland Coffee - B0002'
    }, {
      type: 'paragraph',
      text: 'We can now run an analysis of how many total lbs of coffee we have (3lbs) while determining which coffee bean lot we should use first.'
    }, {
      type: 'paragraph',
      text: 'In one of our future pieces we will discuss the next steps of inventory management: forecasting. Specifically answering the questions: What inventory do I need in the next few weeks? What inventory do I need to buy?'
    }],
    ctaTitle: 'Get help with your inventory management',
    ctaDescription: 'Want to learn more about how Thruline can help with your inventory management? Reach out for a private consultation.',
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Book a Consultation'
  },
  'inventory-management-forecasting': {
    title: 'Inventory Management: Forecasting',
    date: 'October 15, 2024',
    readTime: '4 min read',
    category: 'Inventory',
    author: 'Alex',
    image: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/67b763cb7b5d1973b30c0056_667654688d060396e03d182a-helcim-payments-p_zZditHECg-unsplash.jpg',
    blocks: [{
      type: 'paragraph',
      text: 'This is a follow up to our first post — Inventory Management: What do I have — and our webinar covering these topics.'
    }, {
      type: 'paragraph',
      text: "So once you know what parts you're tracking and where they are, what do you do with that information?"
    }, {
      type: 'paragraph',
      text: "First you need to understand what you're using. If we are a factory where we are producing goods we can do that by tracking what inventory we use to build our product on a manufacturing order. We know what inventory is on a manufacturing order by linking a bill of material to a manufacturing order."
    }, {
      type: 'paragraph',
      text: "If a bill of materials (commonly referred to as a BoM) is the recipe for a particular part, the manufacturing order (or MO) is the particular cake we're making that day. Your manufacturing orders need to detail which exact ingredients you're using from inventory. This includes the part number, the location and any serial numbers if applicable. This ensures that you are removing inventory from your system as you complete your builds."
    }, {
      type: 'paragraph',
      text: "If you aren't producing goods in a factory, you're selling goods. This should be detailed on a sales order. This is commonly done via Point of Sale (PoS) solutions that automatically remove the sold goods from your inventory."
    }, {
      type: 'paragraph',
      text: 'In addition to tracking what you actually used, manufacturing and sales orders are essential to begin planning what you need to purchase in the future. You can look at historical orders to determine when you have high and low seasons, maybe you need to stack more Christmas decorations during December, but you can also see what orders you have planned that have yet to be completed.'
    }, {
      type: 'paragraph',
      text: 'In manufacturing it is common to know what you have to build weeks, or months, in advance. By tracking that information on a manufacturing order, you can tell your purchasing team in advance what they need to buy by comparing the MO requirements to what is in inventory. What you need to buy = what you need to build - what you have. This level of tracking is called demand planning and is one of the components of material resource planning (MRP).'
    }, {
      type: 'paragraph',
      text: 'Tracking your orders can also help your finance team with revenue and cash flow forecasting. By understanding what you will sell (planned orders) and what you need to buy (the materials calculated from your demand planning) you now have a physical basis for your cash flow projections.'
    }, {
      type: 'paragraph',
      text: "Knowing what you have is super important not just for running an efficient business — it's an important cornerstone for giving your business the ability to forecast what will happen in the future."
    }],
    ctaTitle: 'Improve your inventory forecasting',
    ctaDescription: 'Want to learn more about how Thruline can help with your inventory forecasting? Reach out for a private consultation.',
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Book a Consultation'
  },
  'project-costing---why-is-it-so-difficult': {
    title: 'Project Costing - Why is it so difficult',
    date: 'October 15, 2024',
    readTime: '4 min read',
    category: 'Manufacturing',
    author: 'Alex',
    image: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/67b7638e4820d28d5cfda86a_66e9e4c7a1fe965b31180a76-daria-nepriakhina-zoCDWPuiRuA-unsplash.jpg',
    blocks: [{
      type: 'paragraph',
      text: 'Determining the entire cost of a project — regardless of the type of project — is extremely difficult. You need to track:'
    }, {
      type: 'ordered-list',
      items: ['Tasks', 'Expenses (Invoices and Purchase Orders)', 'Time', 'Contracts and Change Orders']
    }, {
      type: 'paragraph',
      text: 'Managing this data is a time consuming endeavor. Even if you have individual systems in place to manage each piece, you need to integrate that information together to ensure all of the data syncs together. Even if the data is synced between systems, you still need to export the data to Excel, or use a third party analytics tool, to get any useful information.'
    }, {
      type: 'paragraph',
      text: "Here's an example — a company manages their project tasks in ClickUp, tracks their billing in Quickbooks, and handles contract change orders and document signing via Docusign. How would they calculate their expected cost of a change order?"
    }, {
      type: 'paragraph',
      text: 'First they would open Docusign to get the language of the change order. Then, they would author the tasks needed to complete the change order in ClickUp and assign those to the team. Now that the change order has a breakdown and task estimation, how would you get an estimated cost?'
    }, {
      type: 'paragraph',
      text: "You would need to go into Quickbooks and assign the expected hourly rate from Quickbooks into each of the tasks in Clickup. Clickup doesn't have a great sum rollup feature, so even if Quickbooks was integrated into Clickup, and I could get an estimated cost per task inside Clickup, I would then need to export my costed tasks from ClickUp into a 3rd party graphing/analytics software, or Excel."
    }, {
      type: 'paragraph',
      text: "That's a lot of manual work, and potential errors. When handling so many moving pieces, integrations between systems don't solve the problem because none of the systems in play are designed to answer questions that take in information from a variety of different systems."
    }, {
      type: 'paragraph',
      text: 'In an ideal world, there would be a single system that allows you to:'
    }, {
      type: 'ordered-list',
      items: ['Create, assign and manage your tasks', 'Track your billing and invoices', 'Show analysis of your estimated and actual project costs', 'Handle sending information to your other systems (such as Docusign and Quickbooks)']
    }, {
      type: 'paragraph',
      text: "Enter Thruline — our ERP is designed to help manage every facet of your business, including your projects. Our focus is ensuring that you can streamline all of your information in a single place — including analysis and integrations into other systems. With Thruline you can estimate your projects, track and add your financial information, and when it's complete automatically generate and send contracts out for signature via Docusign. From the same screen generate and send invoices via the provider of your choice, such as Quickbooks."
    }, {
      type: 'paragraph',
      text: 'This allows you to handle all of your information in a single place, and have a single view of your timelines — without Excel.'
    }, {
      type: 'paragraph',
      text: "At Thruline we're not just built to manage your projects — we're built to manage your business, which is why we've tackled the hardest part about business software, making sure you can use all of your company's data from a single place."
    }],
    ctaTitle: 'Simplify your project costing',
    ctaDescription: 'See how Thruline can help you track tasks, expenses, time, and contracts — all from a single place.',
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Book a Consultation'
  },
  'thruline-announcement': {
    title: 'Thruline Announcement',
    date: 'October 15, 2024',
    readTime: '2 min read',
    category: 'Company',
    author: 'Alex',
    image: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/67b7647cd5f6bdacb033ab4d_66443745d22843965154e625-Funding%20Announcement.png',
    blocks: [{
      type: 'paragraph',
      text: "At Thruline we're building an ERP system that every small business can (actually) afford, with an implementation timeframe measured in days, not months."
    }, {
      type: 'paragraph',
      text: "We're excited to announce a $1.3 million raise from our exciting partners, including GoAhead Ventures, Antler, Race Capital, and Sunset Ventures."
    }, {
      type: 'paragraph',
      text: "Our team has experienced managing the complex UIs, broken integrations, and never ending implementations that existing ERPs entail. We're excited to have built a solution that solves these issues and makes managing our business fun (yep, we run Thruline using Thruline!)"
    }, {
      type: 'paragraph',
      text: "Thruline's goal is to get companies out of spreadsheets and give them time back to actually focus on growing their businesses. With Thruline's streamlined ERP we get our customers up and running in days, not months."
    }, {
      type: 'paragraph',
      text: "We're launching our self sign up environment in June where you can try out our offerings. Sign up for the waitlist and join our Discord Server where you can meet Thruline users and ask questions about the platform."
    }, {
      type: 'paragraph',
      text: "Whether it's managing your project costs, printing QR codes for your inventory, tracking digital work instructions, or connecting your CAD to our PLM — Thruline is here for your business."
    }, {
      type: 'paragraph',
      text: "On behalf of the entire Thruline team — we can't wait to get started!"
    }, {
      type: 'paragraph',
      text: '-Alex'
    }],
    ctaTitle: 'Get started with Thruline',
    ctaDescription: 'Try Thruline for your business — no credit card required.',
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Talk to Our Team'
  },
  'what-does-customizable-software-actually-mean': {
    title: 'What does customizable software actually mean?',
    date: 'October 15, 2024',
    readTime: '4 min read',
    category: 'ERP',
    author: 'Alex',
    image: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/67b764425127d4c5769c9f48_6648edd002e10409977ba88a-campaign-creators---kQ4tBklJI-unsplash.jpg',
    blocks: [{
      type: 'paragraph',
      text: 'Let\'s revisit a topic we\'ve touched on before: What does "customizable software" really mean?'
    }, {
      type: 'paragraph',
      text: 'These days, every software company — especially with help from Cursor to Loveable — offers some level of customization. Whether it\'s connecting via APIs, setting up event-based integrations, defining custom fields, or linking through tools like Zapier, customization is everywhere. But how does Thruline stand out? Aren\'t we just another "customizable" business software?'
    }, {
      type: 'paragraph',
      text: "We think about customization like the levels of self-driving cars. Here's the breakdown:"
    }, {
      type: 'subheading',
      text: 'Level 1: Simple Customization'
    }, {
      type: 'paragraph',
      text: 'Rename fields like "Part Number" to "Product." You can send data out via APIs, but it needs custom coding and upkeep. Examples: Calendly.'
    }, {
      type: 'subheading',
      text: 'Level 2: Partial Customization'
    }, {
      type: 'paragraph',
      text: 'Add new data fields or attributes, but need third-party tools (Zapier, Parabola) or custom coding to share data. Examples: Quickbooks, Fishbowl.'
    }, {
      type: 'subheading',
      text: 'Level 3: Conditional Customization'
    }, {
      type: 'paragraph',
      text: 'Add fields, tweak the UI a bit (like hiding fields), create new data objects — but limited ways to use that data without coding. Basic event triggers might be included. Examples: Hubspot, PipeDrive, Jira.'
    }, {
      type: 'subheading',
      text: 'Level 4: High Customization'
    }, {
      type: 'paragraph',
      text: 'Build full data workflows and custom user views. Trigger events in other systems and add basic logic inside the app. Examples: Netsuite, SAP, Benchling.'
    }, {
      type: 'subheading',
      text: 'Level 5: Full Customization'
    }, {
      type: 'paragraph',
      text: 'Create entire custom applications, no code needed. Automate, send info between systems, build logic — all without starting from scratch. Examples: Thruline, Salesforce, ServiceNow, Monday.com.'
    }, {
      type: 'paragraph',
      text: "At Thruline, we don't just help you document requirements or manage changes — we turn those requirements into ready-to-go setups that fit your business perfectly. We give you true customization early on, with complete change management, so you can ditch spreadsheets and focus on growing your business. That's why our setup time is usually half that of other ERPs."
    }, {
      type: 'paragraph',
      text: 'Lots of companies say their software is flexible — few explain what that really means. Want to hear how Thruline users are leveraging our platform? Join our monthly newsletter to stay in the loop!'
    }],
    ctaTitle: 'See Level 5 customization in action',
    ctaDescription: 'Thruline gives you full customization without code. See how it works for your business.',
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Talk to Our Team'
  },
  'thruline-case-study-connecting-engineering-and-erp-data-for-real-world-analytics': {
    title: 'Connecting Engineering and ERP Data for Real-World Analytics',
    date: 'December 2, 2025',
    readTime: '6 min read',
    category: 'Case Study',
    author: 'Alex',
    image: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/692e80024d03440a7693513b_D05qzqBZCRv.webp',
    blocks: [{
      type: 'paragraph',
      text: "When you're dealing with complex engineering projects, having data scattered across different systems isn't just inconvenient: it's a roadblock to making informed decisions. Lucas Smith, an engineer and Thruline customer, found himself facing exactly this challenge until he discovered how Thruline's API could bridge the gap between engineering and business data in ways he never thought possible."
    }, {
      type: 'heading',
      text: 'The Challenge: Data Islands in Engineering'
    }, {
      type: 'paragraph',
      text: "Lucas's team had a problem that's all too familiar in manufacturing and engineering environments. They were drowning in data, but it was siloed across different systems with no easy way to connect the dots."
    }, {
      type: 'paragraph',
      text: '"Previously didn\'t have a good way to match our engineering data with our business ERP data," Lucas explained during our recent conversation. His team would run tests on motors and collect engineering data, but once that information entered their system, they lost crucial business context.'
    }, {
      type: 'paragraph',
      text: "Think about it: You've got a test motor generating performance data, but you can't easily trace back to understand which specific propellant was used, what materials went into manufacturing, or even basic supply chain information. For engineers trying to assess performance, this missing context was like trying to solve a puzzle with half the pieces missing."
    }, {
      type: 'paragraph',
      text: 'The disconnect wasn\'t just frustrating: it was limiting their ability to do meaningful analytics. "We would have a motor and test data, but not know much about it once the engineering data was put into the system," Lucas noted. The team had their internal propellant data on one side and test data on the other, but no reliable way to connect the full analytical chain.'
    }, {
      type: 'heading',
      text: 'The Solution: API-Driven Data Integration'
    }, {
      type: 'paragraph',
      text: 'Enter Thruline\'s API. What Lucas discovered was that by using a simple serial number as the "seed parameter," he could unlock a complete traceability chain that connected engineering and business data seamlessly.'
    }, {
      type: 'paragraph',
      text: '"The easiest way to match our engineering data with our business ERP data is through calling the API for my application," Lucas said. The beauty of the solution wasn\'t just that it worked: it was intuitive once you understood the structure.'
    }, {
      type: 'paragraph',
      text: 'Lucas built a Python client that interfaces with Thruline, constructing specific queries based on the parameters he needs to pull. "Once I got that set up and tested, it was pretty straightforward," he explained. The system allows him to start with a single data point — like a serial number — and build out a complete picture from there. The integration was built, tested and deployed in 3 days.'
    }, {
      type: 'image',
      src: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/692e80722c93934fa460d5f6_D05qzqBZCRv.webp',
      alt: 'Thruline API integration diagram'
    }, {
      type: 'paragraph',
      text: 'Here\'s what makes this approach powerful: instead of manually hunting through multiple systems to piece together information, Lucas can now generate an entire traceability report based on just one piece of data. "The fact that you can build out an entire traceability report based on a single piece of data that you have from another system, that was very helpful," he said.'
    }, {
      type: 'heading',
      text: 'Implementation: From Confusion to Clarity'
    }, {
      type: 'paragraph',
      text: 'Like many engineers encountering ERP systems for the first time, Lucas initially found the learning curve challenging. "Half of it was me being kind of unfamiliar with business systems," he admitted. But once the table schema and format clicked, everything fell into place.'
    }, {
      type: 'paragraph',
      text: '"Once it clicked, it was much like... it was pretty straightforward to work with," Lucas reflected. This speaks to something we see consistently with Thruline implementations: there\'s often an "aha" moment where the logical structure becomes clear, and from that point forward, the system feels intuitive rather than complex.'
    }, {
      type: 'heading',
      text: 'The Bigger Picture: Why This Matters'
    }, {
      type: 'paragraph',
      text: "Lucas's story illustrates a broader trend in modern manufacturing and engineering: the need to break down data silos that have historically separated engineering and business operations. When these systems can talk to each other seamlessly, it unlocks analytical possibilities that neither side could achieve independently."
    }, {
      type: 'paragraph',
      text: '\"It solved a missing link in this overall analysis chain,\" Lucas summarized. That missing link isn\'t unique to his organization — it\'s a challenge facing engineering teams across industries who are trying to make data-driven decisions while working with disconnected systems.'
    }, {
      type: 'paragraph',
      text: 'The API-first approach that Thruline takes makes this kind of integration not just possible, but practical. Rather than requiring massive system overhauls or complex data migration projects, teams can start with targeted use cases and expand organically as they prove value.'
    }, {
      type: 'image',
      src: 'https://cdn.prod.website-files.com/67b56d3f150af8f39e4672fc/692e80722c93934fa460d5f9_Q0Ptq5ziPxk.webp',
      alt: 'Engineering data analytics dashboard'
    }, {
      type: 'paragraph',
      text: "For engineering organizations looking to improve their analytical capabilities, Lucas's experience offers a clear playbook: start with a specific use case (like traceability), identify the key data relationships you need to establish, and leverage API integration to create those connections without disrupting existing workflows."
    }, {
      type: 'paragraph',
      text: "The result is exactly what Lucas achieved: engineers who can focus on engineering problems rather than data hunting, with complete business context to inform their technical decisions. In an industry where the margin between good and great often comes down to having the right information at the right time, that's the kind of competitive advantage that pays dividends far beyond the initial implementation effort."
    }],
    ctaTitle: 'Connect your engineering and business data',
    ctaDescription: "Want to see how Thruline can help connect your engineering and business data? Reach out to our team to explore what's possible for your organization.",
    ctaLink: 'https://calendly.com/alex-thruline/learn-about-thruline',
    ctaLabel: 'Talk to Our Team'
  },
  'the-erp-factory-thesis': {
    title: 'The ERP Factory Thesis: Why We Built Thruline',
    date: 'February 15, 2026',
    readTime: '8 min read',
    category: 'Company',
    image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&q=80&w=1200&h=600',
    blocks: [{
      type: 'paragraph',
      text: "The traditional ERP implementation model is broken. It has been for decades. Discovery takes months, costs spiral out of control, and the result is software that doesn't match how your team actually works. We built Thruline to fix that."
    }, {
      type: 'paragraph',
      text: "Most ERP implementations follow the same pattern: a consulting firm spends weeks interviewing your team, produces a thick requirements document, then spends months configuring an off-the-shelf system to approximate what you described. The meter runs the entire time. By the time you go live, the project is over budget, behind schedule, and the system still doesn't quite fit."
    }, {
      type: 'heading',
      text: "The problem isn't the software. It's the process."
    }, {
      type: 'paragraph',
      text: 'What if you could hand over your operational documents — SOPs, spreadsheets, emails, whatever captures how your team actually works — and get back a fully configured system in weeks instead of months? What if discovery was free, implementation was fixed-price, and every configuration was traced back to a specific requirement from your own documents?'
    }, {
      type: 'heading',
      text: "That's the ERP Factory."
    }, {
      type: 'paragraph',
      text: "Thruline's system ingests your operational documents and reconstructs your workflows automatically. A consultant reviews the output, identifies gaps, and builds a scoped project plan. You get a fixed-price quote before any work begins. If it doesn't make sense, you walk away having lost nothing."
    }, {
      type: 'paragraph',
      text: 'Once you approve the project, the factory builds your system. Every configuration, workflow, and integration is traced back to a specific requirement. Documentation and training materials are generated automatically. A consultant verifies the output before it goes live.'
    }, {
      type: 'paragraph',
      text: 'The result: a fully custom ERP system, built from your own documents, delivered in weeks instead of months, at a fraction of the cost of traditional implementation.'
    }, {
      type: 'paragraph',
      text: "This isn't about replacing ERP software. It's about replacing the broken process that makes ERP implementations expensive, slow, and risky. The factory model makes it possible to deliver custom enterprise software with the speed and predictability that manufacturers deserve."
    }, {
      type: 'paragraph',
      text: "We believe every manufacturer deserves software that works the way they do — not the other way around. That's why we're building the first ERP Factory for the physical world."
    }],
    ctaTitle: 'Read the full thesis',
    ctaDescription: 'Dive deeper into the thinking behind the ERP Factory model.',
    ctaLink: '/resources/thesis',
    ctaLabel: 'Read Our Thesis'
  }
};
export function BlogPostPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const post = slug ? posts[slug] : null;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!post) {
    return <div className="min-h-screen bg-navy text-white pt-32 pb-20 flex items-center justify-center" data-id="element-949" data-mp="3pwtydiy582e">
        <div className="text-center" data-id="element-950" data-mp="3vvaezqmc15lo">
          <h1 className="text-3xl font-bold mb-4" data-id="element-951" data-mp="6ri1tnl1nm3q">Post not found</h1>
          <Link to="/resources/blog" className="text-violet-electric hover:underline" data-id="element-952" data-mp="6p5h52nwixmz">
            
            Back to blog
          </Link>
        </div>
      </div>;
  }
  const isExternal = post.ctaLink?.startsWith('http');
  return <div className="min-h-screen bg-navy text-white pt-32 pb-20" data-id="element-953" data-mp="lfw8yaphtwq3">
      <article className="max-w-3xl mx-auto px-6" data-id="element-954" data-mp="li8xj6prb5c4">
        {/* Back link */}
        <motion.div initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} className="mb-10" data-id="element-955" data-mp="ph7w89kwa5v4">
          
          <Link to="/resources/blog" className="inline-flex items-center gap-2 text-white/40 text-sm hover:text-white transition-colors" data-id="element-956" data-mp="zfe1n15wtu4q">
            
            <ArrowLeftIcon className="w-4 h-4" data-id="element-957" data-mp="14e8pihiqi8vp" />
            Back to blog
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="mb-10" data-id="element-958" data-mp="3663mrs48uaue">
          
          <span className="text-violet-electric text-xs font-semibold tracking-widest uppercase mb-4 block" data-id="element-959" data-mp="1iksie3yj72mw">
            {post.category}
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight mb-6" data-id="element-960" data-mp="1ej6zo1zbfu4s">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-white/30 text-sm" data-id="element-961" data-mp="269uznap96tgv">
            {post.author && <>
                <span data-id="element-962" data-mp="22800abqvuq5q">{post.author}</span>
                <span className="text-white/15" data-id="element-963" data-mp="2291a6cw5x6lv">•</span>
              </>}
            <span className="flex items-center gap-1.5" data-id="element-964" data-mp="1x8ndfr00hj35">
              <CalendarIcon className="w-3.5 h-3.5" data-id="element-965" data-mp="1xb9ldrqte0lw" />
              {post.date}
            </span>
            <span className="flex items-center gap-1.5" data-id="element-966" data-mp="1scpv5f56u2nq">
              <ClockIcon className="w-3.5 h-3.5" data-id="element-967" data-mp="lv2qh6hjcyxc" />
              {post.readTime}
            </span>
          </div>
        </motion.div>

        {/* Hero Image */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="rounded-2xl overflow-hidden mb-12 border border-white/[0.06]" data-id="element-968" data-mp="2gxpoeo0dpr92">
          
          <img src={post.image} alt={post.title} className="w-full h-64 sm:h-80 object-cover" data-id="element-969" data-mp="27136a6zfteaq" />
          
        </motion.div>

        {/* Content */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="space-y-6" data-id="element-970" data-mp="2zne79iqyhh82">
          
          {post.blocks.map((block, i) => {
          if (block.type === 'heading') {
            return <h2 key={i} className="text-2xl font-bold text-white mt-12 mb-4" data-id="element-971" data-mp="2uwpc9tg2i246">
                  
                  {block.text}
                </h2>;
          }
          if (block.type === 'subheading') {
            return <h3 key={i} className="text-xl font-semibold text-white/90 mt-8 mb-3" data-id="element-972" data-mp="3nejwgoisdlv1">
                  
                  {block.text}
                </h3>;
          }
          if (block.type === 'image') {
            return <figure key={i} className="my-10 rounded-xl overflow-hidden border border-white/[0.06]" data-id="element-973" data-mp="16yna8s6ldosq">
                  
                  <img src={block.src} alt={block.alt} className="w-full object-cover" data-id="element-974" data-mp="1lqfcc2hdl6ci" />
                  
                </figure>;
          }
          if (block.type === 'list') {
            return <ul key={i} className="space-y-3 pl-6" data-id="element-975" data-mp="t0700r9itg5u">
                  {block.items.map((item, j) => {
                const boldMatch = item.match(/^(.+?):\s*(.+)$/);
                return <li key={j} className="text-white/70 text-lg leading-relaxed list-disc marker:text-violet-electric/50" data-id="element-976" data-mp="121em8ayriqjk" data-mpi={j}>
                        
                        {boldMatch ? <>
                            <span className="text-white font-medium" data-id="element-977" data-mp="2deh73i7wa29j">
                              {boldMatch[1]}:
                            </span>{' '}
                            {boldMatch[2]}
                          </> : item}
                      </li>;
              })}
                </ul>;
          }
          if (block.type === 'ordered-list') {
            return <ol key={i} className="space-y-3 pl-6" data-id="element-978" data-mp="2857ru7mshqm3">
                  {block.items.map((item, j) => {
                const boldMatch = item.match(/^(.+?):\s*(.+)$/);
                return <li key={j} className="text-white/70 text-lg leading-relaxed list-decimal marker:text-violet-electric/50" data-id="element-979" data-mp="2387m5xtuqpvi" data-mpi={j}>
                        
                        {boldMatch ? <>
                            <span className="text-white font-medium" data-id="element-980" data-mp="2d27z2lvdkzes">
                              {boldMatch[1]}:
                            </span>{' '}
                            {boldMatch[2]}
                          </> : item}
                      </li>;
              })}
                </ol>;
          }
          if (block.type === 'source-list') {
            return <ol key={i} className="space-y-3 pl-6" data-id="element-981" data-mp="2vvlcve6yip76">
                  {block.items.map((item, j) => <li key={j} className="text-white/70 text-sm leading-relaxed list-decimal marker:text-violet-electric/50" data-id="element-982" data-mp="3fg2dn0nqu0uf" data-mpi={j}>
                    
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-violet-electric underline underline-offset-2 decoration-white/20 hover:decoration-violet-electric/50 transition-colors" data-id="element-983" data-mp="3jhe3n7aj4o35">
                      
                        {item.text}
                      </a>
                    </li>)}
                </ol>;
          }
          return <p key={i} className="text-white/70 text-lg leading-relaxed" data-id="element-984" data-mp="30k4hhidtkwi7" data-mpi={i}>
                {block.text}
              </p>;
        })}
        </motion.div>

        {/* CTA */}
        {post.ctaTitle && <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-16 p-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] text-center" data-id="element-985" data-mp="2rrsvc8pqnv81">
          
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-986" data-mp="1yoqgc60ayx2y">
              {post.ctaTitle}
            </h3>
            <p className="text-white/50 mb-6" data-id="element-987" data-mp="1tx8g5k63t0j1">{post.ctaDescription}</p>
            {isExternal ? <a href={post.ctaLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all" data-id="element-988" data-mp="1oxvswybblrfx">
            
                {post.ctaLabel}
                <ArrowRightIcon className="w-4 h-4" data-id="element-989" data-mp="2hkqanngr6cpp" />
              </a> : <Link to={post.ctaLink || '/resources/thesis'} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all" data-id="element-990" data-mp="2cpayczye8ove">
            
                {post.ctaLabel}
                <ArrowRightIcon className="w-4 h-4" data-id="element-991" data-mp="w9q7e9cfzyf1" />
              </Link>}
          </motion.div>}
      </article>
    </div>;
}